import BaseListPresenter from "../../../../../base/BaseListPresenter";
import { formatDate } from "../../../../../components/common/Converter";
import { findObjectUseCase } from "../../../../../usecases/object";
import { findCurrentUserEmployee, buildHierarchyWhere } from "../../../../../utils/hierarchy";

class LeaveTablePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    const getcurrent = this.view.getcurrent();
    this.limit = 10;
    this.where = { supervisor: getcurrent };
    this.search = {};
    this.filter = {};
    this.include = ["employee"];
    this.keys = []; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  componentDidMount() {
    this.init();
    this.loadEmployeeWhere().then(() => this.getObjects());
  }

  async loadEmployeeWhere() {
    try {
      const employees = await findCurrentUserEmployee(
        this.findObjectUseCase,
        this.view.getCurrentUser(),
      );
      this.view.setWhere(buildHierarchyWhere(employees?.[0]));
    } catch (error) {
      this.view.setWhere({});
      this.view.showError(error);
    }
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  onChangeFilter(type, value, field) {
    // Copy the current filters from the state
    const where = { ...this.view.state.filters };

    // Apply the new filter
    switch (type) {
      case "Pointer":
        if (Object.keys(value).length > 0) {
          where[field] = { id: value.id };
        } else {
          delete where[field]; // Remove filter if no value
        }
        break;
      case "Boolean":
        if (value !== null) {
          where[field] = value;
        } else {
          delete where[field]; // Remove filter if no value
        }
        break;
      default:
        if (value) {
          where[field] = { $regex: value, $options: "i" };
        } else {
          delete where[field]; // Remove filter if no value
        }
    }
    console.log("where: ", this.view.state.filters);
    this.view.setState({ filters: where }, () => {
      this.filterSubmit(this.view.state.filters);
    });
  }

  async GenerateCSV() {
    const query = this.createQuery();
    query.limit = 0;
    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      query
    );

    let csvContent =
      "EMPLOYEE,LEAVE TYPE,START DATE,END DATE,DURATION,STATUS\n"; // CSV header

    objects.forEach((item) => {
      const employee = `${item.employee.Firstname} ${item.employee.Middlename} ${item.employee.surname}`;
      const leaveType = `${item.selectedType}`;
      const startTime = `${formatDate(item.startdate)}`;
      const endTime = item.enddate ? `${formatDate(item.enddate)}` : "";
      const duration = `${item.duration}`;
      const stats = `${item.stats}`;
      csvContent += `${employee},${leaveType},${startTime},${endTime},${duration},${stats}\n`;
    });

    // Create a blob from CSV content
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Leave Report-${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async GenerateExcel() {
    const query = this.createQuery();
    query.limit = 0;

    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      query
    );

    // Define the basic XML structure for an Excel file
    let excelContent = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office"
                xmlns:x="urn:schemas-microsoft-com:office:excel"
                xmlns="http://www.w3.org/TR/REC-html40">
          <head>
              <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8">
              <meta name="ProgId" content="Excel.Sheet">
              <meta name="Generator" content="Microsoft Excel 11">
          </head>
          <body>
              <table border="1">
                  <tr>
                      <th>EMPLOYEE</th>
                      <th>LEAVE TYPE</th>
                      <th>START DATE</th>
                      <th>END DATE</th>
                      <th>DURATION</th>
                      <th>STATUS</th>
                  </tr>`;

    // Add rows dynamically
    objects.forEach((item) => {
      const employee = this.escapeXml(
        `${item.employee.Firstname} ${item.employee.Middlename} ${item.employee.surname}`
      );
      const leaveType = this.escapeXml(`${item.selectedType}`);
      const startTime = this.escapeXml(`${formatDate(item.startdate)}`);
      const endTime = item.enddate
        ? this.escapeXml(`${formatDate(item.enddate)}`)
        : this.escapeXml("");
      const duration = this.escapeXml(`${item.duration}`);
      const stats = this.escapeXml(`${item.stats}`);
      excelContent += `
                  <tr>
                      <td>${employee}</td>
                      <td>${leaveType}</td>
                      <td>${startTime}</td>
                      <td>${endTime}</td>
                      <td>${duration}</td>
                      <td>${stats}</td>
                  </tr>`;
    });

    excelContent += `
              </table>
          </body>
          </html>`;

    // Create a blob from the Excel content
    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Leave Report-${new Date().toISOString().split("T")[0]}.xls`
    );

    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Helper function to escape XML special characters
  escapeXml(value) {
    if (!value) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  }

  async GeneratePDF() {
    const query = this.createQuery();
    query.limit = 0;

    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      query
    );

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const printData = `
          <html>
          <head>
            <style>
  
              @page {
                  size: auto;   /* auto is the initial value */
                  margin: 0;     /* this affects the margin in the printer settings */
                  visibility: hidden;
              }
  
              body {
                margin: 2rem
              }
  
              table {
                border-collapse: collapse;
                width: 100%;
                margin-top: 1rem
              }
              th, td {
                border: 1px solid #dddddd;
                text-align: left;
                padding: 8px;
              }
              th {
                background-color: #f2f2f2;
              }
            </style>
          </head>
            <body>
            <img src="${"/BI.png"}" width="100%" height="130" />
              <table>
                <thead>
                  <tr>
                  <th>EMPLOYEE</th>
                  <th>TIME IN</th>
                  <th>TIME OUT</th>
                  <th>DURATION</th>
                  </tr>
                </thead>
                <tbody>
                  ${objects
                    .map((item) => {
                      const employee = `${item.employee.Firstname} ${item.employee.Middlename} ${item.employee.surname}`;
                      return `
                    <tr>
                      <td>${employee}</td>
                      <td>${formatDate(item.startdate)}</td>
                            <td>${formatDate(item.enddate)}</td>
                            <td>${item.duration}</td>
                    </tr>
                  `;
                    })
                    .join("")}
                </tbody>
              </table>
            </body>
          </html>
        `;

    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(printData);
    iframe.contentWindow.document.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }
}
export default LeaveTablePresenter;
